import { z } from "zod";
import {
  SponsoredDisplayAudienceTargetSchema,
  SponsoredDisplayContextualTargetSchema,
  SponsoredDisplayOptimizationGoalSchema,
  SponsoredDisplayPurchasesLookbackDaysSchema,
  SponsoredDisplayPurchasesRemarketingSourceSchema,
  SponsoredDisplayViewsLookbackDaysSchema,
  SponsoredDisplayViewsRemarketingSourceSchema,
} from "./sponsored-display-creation";

const SponsoredDisplayAsinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{10}$/);

const SponsoredDisplayProductIdentitySchema = z
  .object({
    asin: SponsoredDisplayAsinSchema,
    sku: z.string().trim().min(1).nullable().optional(),
  })
  .strict();

/** The UI and the Amazon recommendation request share this 500-product ceiling. */
export const SPONSORED_DISPLAY_MAX_PRODUCTS = 500;
export const SPONSORED_DISPLAY_MAX_BID_RECOMMENDATION_TARGETS = 100;

export const SponsoredDisplayProductEligibilityRequestSchema = z
  .object({ products: z.array(SponsoredDisplayProductIdentitySchema).min(1).max(500) })
  .strict();
export type SponsoredDisplayProductEligibilityRequest = z.infer<
  typeof SponsoredDisplayProductEligibilityRequestSchema
>;

export const SponsoredDisplayContextualRecommendationsRequestSchema = z
  .object({ advertisedAsins: z.array(SponsoredDisplayAsinSchema).min(1).max(500) })
  .strict();
export type SponsoredDisplayContextualRecommendationsRequest = z.infer<
  typeof SponsoredDisplayContextualRecommendationsRequestSchema
>;

export const SponsoredDisplayAudienceRecommendationsRequestSchema = z.object({}).strict();
export type SponsoredDisplayAudienceRecommendationsRequest = z.infer<
  typeof SponsoredDisplayAudienceRecommendationsRequestSchema
>;

/** Builds a submission-safe target; no Amazon call is needed for these known expressions. */
export const SponsoredDisplayRemarketingTargetRequestSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("views_remarketing"),
      source: SponsoredDisplayViewsRemarketingSourceSchema,
      lookbackDays: SponsoredDisplayViewsLookbackDaysSchema.default(30),
    })
    .strict(),
  z
    .object({
      type: z.literal("purchases_remarketing"),
      source: SponsoredDisplayPurchasesRemarketingSourceSchema,
      lookbackDays: SponsoredDisplayPurchasesLookbackDaysSchema.default(30),
    })
    .strict(),
]);
export type SponsoredDisplayRemarketingTargetRequest = z.infer<
  typeof SponsoredDisplayRemarketingTargetRequestSchema
>;

/**
 * Amazon accepts no more than 100 bid clauses per request. The selected
 * advertised products remain capped at the product-selection limit instead.
 */
export const SponsoredDisplayBidRecommendationsRequestSchema = z
  .object({
    advertisedAsins: z.array(SponsoredDisplayAsinSchema).min(1).max(500),
    optimizationGoal: SponsoredDisplayOptimizationGoalSchema,
    creativeType: z.enum(["IMAGE", "VIDEO"]).nullable().default(null),
    targets: z
      .array(SponsoredDisplayContextualTargetSchema)
      .min(1)
      .max(SPONSORED_DISPLAY_MAX_BID_RECOMMENDATION_TARGETS),
  })
  .strict();
export type SponsoredDisplayBidRecommendationsRequest = z.infer<
  typeof SponsoredDisplayBidRecommendationsRequestSchema
>;

export const SponsoredDisplayAudienceBidRecommendationsRequestSchema = z
  .object({
    advertisedAsins: z.array(SponsoredDisplayAsinSchema).min(1).max(500),
    optimizationGoal: SponsoredDisplayOptimizationGoalSchema,
    creativeType: z.enum(["IMAGE", "VIDEO"]).nullable().default(null),
    targets: z
      .array(SponsoredDisplayAudienceTargetSchema)
      .min(1)
      .max(SPONSORED_DISPLAY_MAX_BID_RECOMMENDATION_TARGETS),
  })
  .strict();
export type SponsoredDisplayAudienceBidRecommendationsRequest = z.infer<
  typeof SponsoredDisplayAudienceBidRecommendationsRequestSchema
>;
